import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { EMPTY_ACTION } from "@/modules/renters/components/constants/paymentConstants";
import { Suspense, useState, useEffect, useCallback } from "react";
import { ICompanyB2B } from "@/types/companyB2B.type";
import ModalCreateCompanyB2B from "../modals/ModalCreateCompanyB2B";
import { getCompanyId } from "@/common/services/company.api";
import { getEmpresasB2B } from "@/common/services/company-b2b.api";
import { useNavigate } from "react-router-dom";

const STATUS_LABELS = {
  ACTIVE: "Ativo",
  INACTIVE: "Inativo",
};

const headers = {
  companyId: "Cidade",
  socialReason: "Razão Social",
  name: "Nome da Empresa",
  cnpj: "CNPJ",
  qtdVehicle: "Quant. Veiculos",
  qtdContract: "Quant. Contratos",
};

type ActionItem = {
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action: (data: any) => any; // Ou (data: unknown) => unknown
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  disabled?: (data: any) => boolean;
  permission?: string;
};

export default function B2BRentalsTable() {
  const companyId = getCompanyId();
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState<ICompanyB2B[]>([]);

  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "socialReason,name,cnpj",
    orderBy: "socialReason",
    itemsPerPage: "10",
  });

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => {
    loadData(filters, page);
    setAction(EMPTY_ACTION);
  };

  const loadData = useCallback(
    async (currentFilters: TableFiltersState, currentPage: number) => {
      setIsLoading(true);
      setCompanies([]);

      const filtersCopy: Record<string, unknown> = { ...currentFilters };
      const statusFilter = filtersCopy.status as string[] | undefined;
      if (Array.isArray(statusFilter) && statusFilter.length > 0) {
        filtersCopy.status = statusFilter.join(",");
      } else {
        delete filtersCopy.status;
      }
      const itemsPerPageNumber = Number(currentFilters.itemsPerPage ?? 10);
      const apiFilters = {
        ...filtersCopy,
        page: currentPage,
        itemsPerPage: itemsPerPageNumber,
      };
      const result = await getEmpresasB2B(apiFilters);

      setData(() => ({
        itemsPerPage: itemsPerPageNumber,
        totalItems: result.totalItems,
      }));

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message ||
          "Erro ao buscar empresas B2B."
        );
        setIsLoading(false);
        return;
      }

      setCompanies(result.data);
      setIsLoading(false);
    },
    [notifyError]
  );

  useEffect(() => {
    loadData(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, companyId]);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setPage(1);
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  const onEmpresaSave = (empresa: ICompanyB2B) => {
    setCompanies((prev) => {
      const index = prev.findIndex((c) => c._id === empresa._id);
      if (index !== -1) {
        const updatedCompanies = [...prev];
        updatedCompanies[index] = empresa;
        return updatedCompanies;
      }
      loadData(filters, page);
      return prev;
    });
    cleanActionData();
  };

  const actions = [
    {
      label: "Editar",
      action: (data: ICompanyB2B) =>
        setAction({ eventName: "edit", data: data }),
    },
  ] as ActionItem[];

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Empresas B2B"
          placeholder="Digite a Razão Social, Nome Fantasia ou CNPJ."
          statusOptions={STATUS_LABELS}
          newItemLabel="Nova Empresa B2B"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          entity="companiesb2b"
          onSearch={handleFilterChange}
          tableData={companies}
        />

        <GenericTable
          headers={headers}
          data={companies}
          actions={actions}
          setFilters={({ orderBy }) => {
            handleFilterChange({ orderBy });
          }}
          renders={{
            name: (name, row) => (
              <a
                href={`/locacoesB2B/details/${(row as ICompanyB2B)._id}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/locacoesB2B/details/${(row as ICompanyB2B)._id}`);
                }}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline"
              >
                {name as string}
              </a>
            ),
          }}
          entity="vehicle"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />

        {isAction("create") && (
          <ModalCreateCompanyB2B
            isOpen={action.eventName === "create"}
            data={action.data as ICompanyB2B}
            onClose={cleanActionData}
            onSave={onEmpresaSave}
          />
        )}
      </div>
    </Suspense>
  );
}
